import type { ToolDefinition } from './types';

// All known target-size slugs (manual + generated) ordered by size for adjacency linking
const targetSizeOrder = [
  'reducir-imagen-10kb', 'reducir-imagen-20kb', 'reducir-imagen-25kb',
  'reducir-imagen-30kb', 'reducir-imagen-50kb', 'reducir-imagen-75kb',
  'reducir-imagen-100kb', 'reducir-imagen-150kb', 'reducir-imagen-200kb',
  'reducir-imagen-250kb', 'reducir-imagen-300kb', 'reducir-imagen-500kb',
  'reducir-imagen-1mb', 'reducir-imagen-2mb', 'reducir-imagen-3mb',
  'reducir-imagen-5mb',
];

const fotoTargetSizeOrder = [
  'reducir-foto-50kb', 'reducir-foto-100kb', 'reducir-foto-200kb',
  'reducir-foto-300kb', 'reducir-foto-500kb',
];

const compressionSynonyms = [
  'comprimir-imagen', 'comprimir-foto', 'comprimir-imagen-online',
  'comprimir-imagen-sin-perder-calidad', 'comprimir-fotos-online',
  'comprimir-imagen-gratis', 'comprimir-foto-online', 'comprimir-imagen-jpg',
  'reducir-peso-imagen', 'reducir-tamano-imagen', 'reducir-peso-foto',
];

const useCaseSlugs = [
  'comprimir-imagen-para-email', 'comprimir-imagen-para-whatsapp',
  'comprimir-imagen-para-cv', 'comprimir-imagen-para-web',
  'comprimir-imagen-para-web-gratis', 'comprimir-imagen-para-formulario',
  'comprimir-imagen-para-pdf', 'comprimir-foto-whatsapp',
];

const optimizationSlugs = [
  'optimizar-imagen-online', 'optimizar-imagen-para-web',
  'optimizar-imagen-wordpress', 'optimizar-imagen-shopify',
  'optimizar-imagen-woocommerce', 'optimizar-imagen-seo',
  'optimizar-imagen-para-blog',
];

const cropSlugs = [
  'recortar-imagen', 'recortar-imagen-online', 'recortar-imagen-instagram',
  'recortar-imagen-cuadrada', 'recortar-imagen-16-9',
];

const rotateSlugs = [
  'rotar-imagen', 'girar-foto', 'voltear-imagen', 'rotar-imagen-90-grados',
];

const resizePercentSlugs = [
  'redimensionar-imagen-50-porciento', 'redimensionar-imagen-25-porciento',
  'aumentar-tamano-imagen',
];

const batchSlugs = [
  'comprimir-varias-imagenes',
];

function getAdjacentSlugs(slug: string, list: string[], count: number = 2): string[] {
  const idx = list.indexOf(slug);
  if (idx === -1) return [];
  const result: string[] = [];
  if (idx > 0) result.push(list[idx - 1]);
  if (idx < list.length - 1) result.push(list[idx + 1]);
  // Add one more if we only got 1
  if (result.length < count) {
    if (idx > 1) result.push(list[idx - 2]);
    else if (idx < list.length - 2) result.push(list[idx + 2]);
  }
  return result;
}

export function assignRelatedSlugs(tools: ToolDefinition[]): ToolDefinition[] {
  const allSlugs = new Set(tools.map(t => t.slug));

  return tools.map(tool => {
    // If manual tool already has relatedSlugs, keep them
    if (tool.relatedSlugs.length > 0) return tool;

    const related: string[] = [];

    // Target-size pages
    if (tool.pageType === 'target-size') {
      const isImagen = tool.slug.startsWith('reducir-imagen-');
      const isFoto = tool.slug.startsWith('reducir-foto-');

      if (isImagen) {
        related.push(...getAdjacentSlugs(tool.slug, targetSizeOrder));
        // Link to corresponding "foto" variant if exists
        const fotoVariant = tool.slug.replace('reducir-imagen-', 'reducir-foto-');
        if (allSlugs.has(fotoVariant)) related.push(fotoVariant);
      } else if (isFoto) {
        related.push(...getAdjacentSlugs(tool.slug, fotoTargetSizeOrder));
        // Link to corresponding "imagen" variant
        const imagenVariant = tool.slug.replace('reducir-foto-', 'reducir-imagen-');
        if (allSlugs.has(imagenVariant)) related.push(imagenVariant);
      }
      related.push('comprimir-imagen');
    }

    // Compression synonym pages
    if (compressionSynonyms.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = compressionSynonyms.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 3));
      if (!related.includes('comprimir-jpg')) related.push('comprimir-jpg');
    }

    // Use-case pages
    if (useCaseSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = useCaseSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      related.push('comprimir-imagen');
      if (!related.includes('comprimir-foto')) related.push('comprimir-foto');
    }

    // Optimization pages
    if (optimizationSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = optimizationSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      related.push('optimizar-imagen-online');
      if (!related.includes('comprimir-imagen-para-web')) related.push('comprimir-imagen-para-web');
    }

    // Crop pages
    if (cropSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = cropSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('redimensionar-imagen')) related.push('redimensionar-imagen');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // Rotate pages
    if (rotateSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = rotateSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('recortar-imagen')) related.push('recortar-imagen');
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
    }

    // Resize-percent pages
    if (resizePercentSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      const others = resizePercentSlugs.filter(s => s !== tool.slug && allSlugs.has(s));
      related.push(...others.slice(0, 2));
      if (allSlugs.has('redimensionar-imagen')) related.push('redimensionar-imagen');
    }

    // Batch pages
    if (batchSlugs.includes(tool.slug) && tool.relatedSlugs.length === 0) {
      if (allSlugs.has('comprimir-imagen')) related.push('comprimir-imagen');
      if (allSlugs.has('comprimir-fotos-online')) related.push('comprimir-fotos-online');
      if (allSlugs.has('comprimir-imagen-gratis')) related.push('comprimir-imagen-gratis');
    }

    // Dedupe and limit to 4
    const uniqueRelated = [...new Set(related.filter(s => s !== tool.slug && allSlugs.has(s)))].slice(0, 4);

    return { ...tool, relatedSlugs: uniqueRelated };
  });
}
